import { useState } from "react";
import { useAuth } from "@clerk/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeftRight, ChevronDown, X } from "lucide-react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger,
  Skeleton,
  toast,
} from "@ndd/ui";
import { cn } from "@/lib/utils";
import { ApiRequestError, authenticatedApi } from "@/lib/api";
import { avatarClass, initials } from "@/lib/avatar";
import { formatLocalDate, formatTimestamp } from "../../../../shared/local-date";

// NO-378 — the manual session-mapping control. Server truth for who may see
// this is session read access; the chip only renders on pages those users can
// already open. Wire types mirror the server's `SessionLinkState` /
// `RdPanelPatient` in `server/lib/session-links/manual.ts` by name.

export interface SessionLinkState {
  patientId: string | null;
  patientName: string | null;
  appointmentId: string | null;
  appointmentDate: string | null;
  linkedByName: string | null;
  linkedAt: string | null;
}

interface RdPanelPatient {
  id: string;
  displayName: string | null;
  lastSeenAt: string | null;
}

interface PatientLinkChipProps {
  sessionId: string;
  rdId: string;
  rdName: string;
  patientLink: SessionLinkState;
}

function firstName(name: string): string {
  return name.trim().split(/\s+/)[0];
}

function apiErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof ApiRequestError) {
    const body = error.body as { error?: string } | undefined;
    if (body?.error) return body.error;
  }
  return fallback;
}

interface PatientPickerProps {
  rdId: string;
  rdName: string;
  enabled: boolean;
  busy: boolean;
  onPick: (patientId: string) => void;
}

function PatientPicker({ rdId, rdName, enabled, busy, onPick }: PatientPickerProps) {
  const { getToken } = useAuth();
  const [search, setSearch] = useState("");

  const patients = useQuery({
    queryKey: ["rd-patients", rdId],
    queryFn: () =>
      authenticatedApi<{ data: RdPanelPatient[] }>(getToken, `/api/rds/${rdId}/patients`),
    enabled,
  });

  const filtered = (patients.data?.data ?? []).filter((patient) =>
    (patient.displayName ?? "").toLowerCase().includes(search.trim().toLowerCase()),
  );

  return (
    <PopoverContent className="w-[320px] p-0" align="start">
      <Command shouldFilter={false}>
        <CommandInput
          placeholder={`Search ${firstName(rdName)}'s patients…`}
          value={search}
          onValueChange={setSearch}
        />
        <CommandList>
          {patients.isPending ? (
            <div className="space-y-2 p-3">
              {[0, 1, 2].map((i) => (
                <div key={i} className="flex items-center gap-2.5">
                  <Skeleton className="h-6 w-6 rounded-full" />
                  <div className="flex-1 space-y-1.5">
                    <Skeleton className="h-3 w-28" />
                    <Skeleton className="h-2.5 w-20" />
                  </div>
                </div>
              ))}
            </div>
          ) : patients.isError ? (
            <div className="px-3 py-4 text-center text-xs text-sand-500">
              Could not load patients — close and try again.
            </div>
          ) : (
            <>
              <CommandEmpty>
                <span className="block px-2 text-xs text-sand-500">
                  No patient named &ldquo;{search}&rdquo; in {rdName}&apos;s panel.
                  <span className="mt-1 block text-[11px] text-sand-400">
                    Patients appear here once they have an appointment with this RD.
                  </span>
                </span>
              </CommandEmpty>
              <CommandGroup>
                {filtered.map((patient) => (
                  <CommandItem
                    key={patient.id}
                    value={patient.id}
                    disabled={busy}
                    onSelect={() => onPick(patient.id)}
                  >
                    <span
                      className={cn(
                        "mr-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold text-white",
                        avatarClass(patient.id),
                      )}
                    >
                      {initials(patient.displayName ?? "?")}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[13px] font-medium">
                        {patient.displayName ?? "Unnamed patient"}
                      </span>
                      <span className="block text-[11px] text-sand-500">
                        {patient.lastSeenAt
                          ? `Last seen ${formatLocalDate(patient.lastSeenAt, "shortWithYear")}`
                          : "No visits recorded"}
                      </span>
                    </span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </>
          )}
        </CommandList>
        <div className="border-t border-sand-200 px-3 py-2 text-[11px] leading-snug text-sand-400">
          Only {rdName}&apos;s patients are listed. Linking is recorded with your name.
        </div>
      </Command>
    </PopoverContent>
  );
}

export function PatientLinkChip({ sessionId, rdId, rdName, patientLink }: PatientLinkChipProps) {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  const [pickerOpen, setPickerOpen] = useState(false);

  // The session detail query keys on the URL segment (short id) while this
  // component holds the full id, so invalidate the whole "session" family.
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ["session"] });
    void queryClient.invalidateQueries({ queryKey: ["rd-sessions", rdId] });
  };

  const unlink = useMutation({
    mutationFn: () =>
      authenticatedApi<{ data: SessionLinkState }>(getToken, `/api/sessions/${sessionId}/patient-link`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      invalidate();
      toast.success("Removed patient link");
    },
    onError: (error) => {
      // 409 = already unlinked: the desired state holds, so converge quietly.
      if (error instanceof ApiRequestError && error.status === 409) {
        invalidate();
        return;
      }
      toast.error(apiErrorMessage(error, "Could not remove the link — try again"));
    },
  });

  const link = useMutation({
    mutationFn: (patientId: string) =>
      authenticatedApi<{ data: SessionLinkState }>(getToken, `/api/sessions/${sessionId}/patient-link`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ patientId }),
      }),
    onSuccess: ({ data }) => {
      invalidate();
      const attachedDate = data.appointmentDate
        ? formatLocalDate(data.appointmentDate, "shortWithYear")
        : null;
      toast.success(`Linked to ${data.patientName ?? "patient"}`, {
        description: attachedDate
          ? `Matched their ${attachedDate} appointment automatically.`
          : undefined,
        action: {
          label: "Undo",
          onClick: () => unlink.mutate(),
        },
      });
    },
    onError: (error) => {
      invalidate();
      toast.error(apiErrorMessage(error, "Could not link the patient — try again"));
    },
  });

  const busy = link.isPending || unlink.isPending;

  const handlePick = (patientId: string) => {
    setPickerOpen(false);
    link.mutate(patientId);
  };

  // Keyed on the open flag so each open remounts the picker with a fresh
  // search — the popover keeps its children mounted while closed.
  const picker = (
    <PatientPicker
      key={String(pickerOpen)}
      rdId={rdId}
      rdName={rdName}
      enabled={pickerOpen}
      busy={busy}
      onPick={handlePick}
    />
  );

  if (!patientLink.patientId) {
    return (
      <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={busy}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-full border border-dashed border-sand-300 px-2.5 py-0.5",
              "text-xs text-sand-500 transition-colors",
              "hover:border-interactive hover:bg-interactive-light hover:text-interactive",
              pickerOpen && "border-interactive bg-interactive-light text-interactive",
            )}
          >
            <span
              className={cn(
                "h-[7px] w-[7px] shrink-0 rounded-full border-[1.5px] border-sand-400",
                pickerOpen && "border-interactive",
              )}
            />
            No patient linked
            <span className="font-semibold text-interactive">· Link</span>
          </button>
        </PopoverTrigger>
        {picker}
      </Popover>
    );
  }

  return (
    <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
      <DropdownMenu>
        <PopoverAnchor asChild>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              disabled={busy}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-full border border-sand-200 bg-card px-2.5 py-0.5",
                "text-xs text-foreground transition-colors hover:border-sand-300 hover:bg-sand-100",
              )}
            >
              <span className="h-[7px] w-[7px] shrink-0 rounded-full bg-forest-500" />
              {patientLink.patientName ?? "Linked patient"}
              <ChevronDown className="h-3 w-3 text-sand-400" />
            </button>
          </DropdownMenuTrigger>
        </PopoverAnchor>
        <DropdownMenuContent align="start" className="w-[220px]">
          <DropdownMenuItem onSelect={() => setPickerOpen(true)}>
            <ArrowLeftRight className="h-3.5 w-3.5" />
            Change patient
          </DropdownMenuItem>
          <DropdownMenuItem variant="danger" onSelect={() => unlink.mutate()}>
            <X className="h-3.5 w-3.5" />
            Remove link
          </DropdownMenuItem>
          {patientLink.linkedByName && patientLink.linkedAt && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-[10.5px] font-normal leading-snug text-sand-400">
                Linked by {patientLink.linkedByName} ·{" "}
                {formatTimestamp(patientLink.linkedAt, "shortWithYear")}. Changes are recorded.
              </DropdownMenuLabel>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      {picker}
    </Popover>
  );
}
